<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  name: 'UpdatePassword',
});
</script>
<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import type { FormInstance, FormRules } from 'element-plus';
import { Motion } from '../authentication';
import type { UpdatePasswordProps, UpdatePasswordFormData, UpdatePasswordParams } from './types';

;

const props = withDefaults(defineProps<UpdatePasswordProps>(), {
  loading: false,
  title: '',
  logoSrc: '',
  illustrationSrc: '',
  backIconSrc: '',
  showBackButton: true,
  passwordRegex: () => /^(?=.*[a-zA-Z])(?=.*\d).{7,20}$/,
  passwordErrorMessage: '至少七位，必须含字母和数字',
});

const emit = defineEmits<{
  submit: [data: UpdatePasswordParams];
  back: [];
}>();

const { t } = useI18n();
const router = useRouter();

const ruleFormRef = ref<FormInstance>();
const formData = ref<UpdatePasswordFormData>({
  oldPassword: '',
  newPassword: '',
  repeatPassword: '',
});

// 密码验证
const validatePass = (rule: any, value: any, callback: any) => {
  if (value === '') {
    callback(new Error(t('请输入新密码')));
  } else {
    if (!ruleFormRef.value) return;
    if (!props.passwordRegex.test(formData.value.newPassword)) {
      callback(new Error(t(props.passwordErrorMessage)));
    }
    if (formData.value.repeatPassword !== '') {
      ruleFormRef.value.validateField('repeatPassword', () => null);
    }
    callback();
  }
};

// 确认密码验证
const validatePass2 = (rule: any, value: any, callback: any) => {
  if (value === '') {
    callback(new Error(t('请再次输入新密码')));
  } else if (value !== formData.value.newPassword) {
    callback(new Error(t('两次输入不一致')));
  } else {
    callback();
  }
};

// 表单验证规则
const formRules = reactive<FormRules>({
  oldPassword: [{ required: true, message: t('请输入旧密码'), trigger: 'blur' }],
  newPassword: [{ required: true, validator: validatePass, trigger: 'blur' }],
  repeatPassword: [{ required: true, validator: validatePass2, trigger: 'blur' }],
});

// 返回
const handleBack = () => {
  emit('back');
  router.back();
};

// 提交
const handleSubmit = async () => {
  if (!ruleFormRef.value) return;
  
  await ruleFormRef.value.validate((valid) => {
    if (valid) {
      emit('submit', {
        oldPassword: formData.value.oldPassword,
        newPassword: formData.value.newPassword,
      });
    }
  });
};

// 获取当前年份
const currentYear = new Date().getFullYear();
</script>

<template>
  <div class="update-password-container">
    <div class="login-container flex justify-center">
      <!-- 返回按钮 -->
      <div v-if="showBackButton" class="btn-back" @click="handleBack">
        <img v-if="backIconSrc" :src="backIconSrc" alt="back" />
        {{ t('返回') }}
      </div>

      <div class="login-container-box">
        <div class="login flex">
          <!-- 左侧插图 -->
          <div class="img">
            <img v-if="logoSrc" :src="logoSrc" class="logo" alt="logo" />
            <img v-if="illustrationSrc" :src="illustrationSrc" class="illustration" alt="illustration" />
            <slot name="illustration" />
          </div>

          <!-- 右侧表单 -->
          <div class="login-box flex">
            <div class="login-form">
              <!-- 标题 -->
              <Motion>
                <div class="welcome-title">
                  {{ title || $t('buttons.hsUpdatePassword') }}
                </div>
                <slot name="title" />
              </Motion>

              <!-- 表单 -->
              <el-form
                ref="ruleFormRef"
                :model="formData"
                :rules="formRules"
                size="default"
                style="display: flex; flex-direction: column; width: 100%; margin-top: 40px"
                label-position="top"
                @keyup.enter="handleSubmit"
              >
                <!-- 旧密码 -->
                <Motion :delay="100">
                  <el-form-item
                    prop="oldPassword"
                    :label="$t('旧密码')"
                    inline-message
                    show-message
                  >
                    <el-input
                      v-model="formData.oldPassword"
                      show-password
                      :placeholder="$t('请输入旧密码')"
                    />
                  </el-form-item>
                </Motion>

                <!-- 新密码 -->
                <Motion :delay="150">
                  <el-form-item
                    prop="newPassword"
                    :label="$t('新密码')"
                    inline-message
                    show-message
                  >
                    <el-input
                      v-model="formData.newPassword"
                      show-password
                      :placeholder="$t(passwordErrorMessage)"
                    />
                  </el-form-item>
                </Motion>

                <!-- 确认新密码 -->
                <Motion :delay="200">
                  <el-form-item
                    prop="repeatPassword"
                    :label="$t('再次确认新密码')"
                    inline-message
                    show-message
                  >
                    <el-input
                      v-model="formData.repeatPassword"
                      show-password
                      :placeholder="$t(passwordErrorMessage)"
                    />
                  </el-form-item>
                </Motion>

                <!-- 提交按钮 -->
                <Motion :delay="250">
                  <el-form-item style="height: 46px">
                    <slot name="submitButton" :submit="handleSubmit" :loading="loading">
                      <el-button
                        class="w-full login-btn"
                        style="height: 100%; line-height: 20px"
                        type="primary"
                        :loading="loading"
                        @click="handleSubmit"
                      >
                        {{ $t('确认') }}
                      </el-button>
                    </slot>
                  </el-form-item>
                </Motion>
              </el-form>

              <!-- 额外内容 -->
              <slot name="extra" />
            </div>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <footer style="position: fixed; bottom: 10px; font-size: 12px; color: #fff; text-align: center">
        <slot name="footer">
          © 2022-{{ currentYear }} PAX All Rights Reserved
        </slot>
      </footer>

      <!-- 装饰元素 -->
      <div class="bird">
        <slot name="decoration" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.update-password-container {
  width: 100vw;
  height: 100vh;
}

:deep(.el-input-group__append, .el-input-group__prepend) {
  padding: 0;
}

.logo {
  margin-top: 35px;
}

.logo-img {
  margin: 0 auto;
}

.btn-back {
  position: fixed;
  top: 20px;
  left: 40px;
  z-index: 100;
  display: flex;
  align-items: center;
  color: #fff;
  cursor: pointer;

  img {
    width: 28px;
    height: 28px;
    margin-right: 10px;
  }
}
</style>

